import { z } from "zod";
import { ROUTES } from "@lib/routes";
import { activityHref } from "@lib/activities/registry";
import { MODALITIES } from "@lib/learner/modality/modality";
import {
  PASSAGE_FONT_PCT_MAX,
  PASSAGE_FONT_PCT_MIN,
  PASSAGE_FONT_PCT_STEP,
} from "@lib/api/native/contracts/passageFont";
import type { PreferenceField } from "@lib/userPreferencesSchema";

/**
 * In-band agent action protocol (D-13): the tutor stream may end with a
 * `[ACTION{...json}]` token proposing an action. Mirrors the `endToken.ts`
 * `[CONVERSATION_END]` precedent - the LLM can only emit text; nothing
 * executes until the learner taps the confirm chip, whose fetch hits the
 * same authenticated routes the on-page controls use (D-14).
 */

/** Navigation whitelist: the model proposes a key, never a raw path. */
export const NAVIGATE_DESTINATIONS = {
  home: ROUTES.home,
  board: ROUTES.curriculum,
  history: ROUTES.curriculumHistory,
  sessions: ROUTES.sessions,
  activities: ROUTES.activities,
  scenarios: activityHref("scenarios"),
  settings: ROUTES.settings,
  progress: ROUTES.progress,
  review: ROUTES.review,
  profile: ROUTES.profile,
} as const;

export type NavigateDestination = keyof typeof NAVIGATE_DESTINATIONS;

const destinationKeys = Object.keys(NAVIGATE_DESTINATIONS) as [
  NavigateDestination,
  ...NavigateDestination[],
];

/**
 * Lesson-runner deep link, as copied from page data (board/history hrefs).
 * Single-segment since plan 25-01 collapsed the curriculum URL tree: the old
 * four-segment shape is a retired URL that hard-404s, so accepting it here
 * would only let the tutor propose dead links.
 */
const LESSON_HREF_RE = /^\/curriculum\/\d+(\?content=\d+)?$/;

/**
 * Low-risk preferences the tutor may propose changing. Locales and the
 * learner-context tier are deliberately excluded. Values are only
 * shape-checked here; the settings PATCH route's zod schema is the real
 * validator.
 */
export const SETTABLE_PREFERENCES = {
  passageAudioSpeed: "number between 0.5 and 1.2",
  tutorSpeechSpeed: "number between 0.5 and 1.5",
  autoplayTutorAudio: "boolean",
  reducedMotion: "boolean",
  feedbackVerbosity: '"BRIEF" or "DETAILED"',
  vocabRevealMode: '"TAP" or "ALWAYS"',
  passageFontScalePct: `integer percent of the 22px passage base, ${PASSAGE_FONT_PCT_MIN}-${PASSAGE_FONT_PCT_MAX} in steps of ${PASSAGE_FONT_PCT_STEP}`,
} as const satisfies Partial<Record<PreferenceField, string>>;

export type SettablePreferenceKey = keyof typeof SETTABLE_PREFERENCES;

const preferenceKeys = Object.keys(SETTABLE_PREFERENCES) as [
  SettablePreferenceKey,
  ...SettablePreferenceKey[],
];

const modalitySchema = z.enum(MODALITIES);

const actionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("regenerate_slot"),
    modality: modalitySchema,
  }),
  z.object({
    type: z.literal("navigate"),
    destination: z.enum(destinationKeys),
    reason: z.string().max(200).optional(),
  }),
  z.object({
    type: z.literal("open_lesson"),
    href: z.string().regex(LESSON_HREF_RE),
    title: z.string().max(200).optional(),
  }),
  z.object({
    type: z.literal("set_preference"),
    key: z.enum(preferenceKeys),
    value: z.union([z.string().max(40), z.number(), z.boolean()]),
  }),
  z.object({
    type: z.literal("report_issue"),
    summary: z.string().min(1).max(500),
  }),
]);

export type AgentAction = z.infer<typeof actionSchema>;

// [\s\S] instead of the `s` (dotAll) flag: container tsc targets pre-es2018.
const TOKEN_RE = /\[ACTION(\{[\s\S]*?\})\]\s*$/;

/**
 * The outcome-history format belongs to `describeActionOutcome`; only the app
 * writes it. The model sometimes imitates it instead of emitting the ACTION
 * token, fabricating a "completed successfully" claim. Strip it from display so
 * learners never see raw protocol text, and so the fabricated claim does not
 * survive into replayed history and block a real proposal on the next turn.
 */
const FABRICATED_OUTCOME_RE = /\s*\[proposed action:[^\]]*\]\s*/g;

function stripFabricatedOutcomeLines(text: string): string {
  return text
    .replace(FABRICATED_OUTCOME_RE, " ")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/**
 * Strip a trailing action token from tutor text. Malformed JSON, unknown
 * types, or invalid payloads drop silently - the tip text still renders,
 * no chip (research A4).
 */
export function parseActionToken(text: string): { display: string; action: AgentAction | null } {
  const m = text.match(TOKEN_RE);
  if (!m) return { display: stripFabricatedOutcomeLines(text), action: null };
  const display = stripFabricatedOutcomeLines(text.slice(0, m.index));
  try {
    const parsed = actionSchema.safeParse(JSON.parse(m[1]));
    return { display, action: parsed.success ? parsed.data : null }; // malformed → drop silently
  } catch {
    return { display, action: null };
  }
}

/**
 * Durable outcome of a proposed action (CON-123). "busy" is deliberately absent:
 * an in-flight fetch is transient chip state, never persisted or replayed.
 */
export type AgentActionStatus = "pending" | "confirmed" | "failed" | "dismissed";

/** The field that tells one proposal of a given type apart from another. */
function summarizeAction(action: AgentAction): string {
  switch (action.type) {
    case "regenerate_slot":
      return action.modality;
    case "navigate":
      return action.destination;
    case "open_lesson":
      return action.title ?? action.href;
    case "set_preference":
      return `${action.key}=${String(action.value)}`;
    case "report_issue":
      return action.summary;
  }
}

const OUTCOMES: Record<AgentActionStatus, string> = {
  pending: "awaiting the learner's confirmation",
  confirmed: "the learner confirmed it and it completed successfully",
  failed: "the learner confirmed it but it failed",
  dismissed: "the learner declined it",
};

/**
 * Render a settled action as a history line the tutor can read back (CON-123).
 * Without this the model saw only "yes" with no evidence it ever acted, and
 * cheerfully proposed the same action again on the next turn.
 */
export function describeActionOutcome(
  action: AgentAction,
  status: AgentActionStatus = "pending",
): string {
  return `[proposed action: ${action.type} (${summarizeAction(action)}) - ${OUTCOMES[status]}]`;
}

export type ActionInstructionContext = {
  /** Per-modality levels line (same format as the tutor-context "Skill levels:" line). */
  skillLevelsLine: string;
  /** Current page kind from the page context ("lesson" when in a lesson). */
  currentPage?: string | null;
  /** Advertise open_lesson only when the page data carries lesson hrefs. */
  hasLessonHrefs?: boolean;
};

/**
 * System-prompt block teaching the tutor the available actions and the
 * emission format, tailored to what the current page makes possible.
 */
export function buildActionInstructions(ctx: ActionInstructionContext): string {
  const destinations = destinationKeys
    .map((key) => (key === ctx.currentPage ? `${key} (current page)` : key))
    .join(", ");
  const preferences = preferenceKeys
    .map((key) => `${key} (${SETTABLE_PREFERENCES[key]})`)
    .join("; ");

  return [
    "Actions you can propose (the learner confirms with a tap; nothing runs otherwise):",
    ctx.skillLevelsLine ? `Current skill levels: ${ctx.skillLevelsLine}` : null,
    "- regenerate_slot: regenerate the learner's board lesson for a modality (READING, WRITING, SPEAKING, LISTENING) at their current level.",
    `- navigate: take the learner to an app page. Destinations: ${destinations}.`,
    ctx.hasLessonHrefs
      ? "- open_lesson: open a specific lesson using an href copied EXACTLY from the page data (lessonHrefs)."
      : null,
    `- set_preference: change one setting for the learner. Keys: ${preferences}.`,
    "- report_issue: when the learner describes something broken or clearly wrong with the app (not a language question), propose filing a report with a one-sentence summary of the problem.",
    "To propose an action, end your reply with the bracketed token as the FINAL line - nothing may follow it.",
    "Propose at most ONE action, and only when the learner asks for it or it clearly helps; otherwise reply normally with no token.",
    'Never propose an action in reply to a greeting, a thank-you, a clarifying message like "what?", or a complaint about your replies; just respond with no token.',
    'Earlier turns of yours may carry a line like "[proposed action: ... - the learner confirmed it and it completed successfully]". That action is already done: never propose it again, and never say you are about to do it.',
    '"[proposed action: ...]" lines are written by the app after an action settles, never by you: never write one yourself, and never claim an action ran - nothing happens unless you emit the [ACTION{...}] token and the learner confirms the chip it renders.',
    "Examples:",
    '[ACTION{"type":"regenerate_slot","modality":"WRITING"}]',
    '[ACTION{"type":"navigate","destination":"review","reason":"You have cards due for review"}]',
    ctx.hasLessonHrefs
      ? '[ACTION{"type":"open_lesson","href":"/curriculum/42","title":"No mercado"}]'
      : null,
    '[ACTION{"type":"set_preference","key":"passageAudioSpeed","value":0.75}]',
    '[ACTION{"type":"report_issue","summary":"Passage audio fails to play on the reading lesson"}]',
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}
